import { isStrictlyObject } from 'utils/util/util';
import { Filterable } from '../../Filters/types';

// the parts of a column holding plain numeric row values (tenure, ratings,
// deltas...) that make it filterable by value and by range
export type NumericFilterColumn = {
  field: string;
  numeric: true;
  filterDescription: string;
  // the row field holding the number to compare, when the column's own
  // field holds something richer (e.g. a [count, cell content] pair)
  filterField?: string;
  // predefined values of the field (e.g. the rating scale) offered as
  // suggestions; any other number can still be typed in
  filterSuggestions?: { id: string; name: string }[];
  filterIcon?: string;
};

export interface NumericFilterSuggestion extends Filterable {
  name: string;
  icon?: string;
  description: string;
}

export interface NumericFilterConfig {
  numericKeys: Record<string, string>;
  suggestions: NumericFilterSuggestion[];
}

export const isNumericFilterColumn = (column: {
  [key: string]: unknown;
}): column is NumericFilterColumn =>
  column?.numeric === true &&
  typeof column?.field === 'string' &&
  typeof column?.filterDescription === 'string';

export const buildNumericFilterConfig = (
  columns: { [key: string]: unknown }[]
): NumericFilterConfig => {
  const numericKeys: Record<string, string> = {};
  const suggestions: NumericFilterSuggestion[] = [];

  for (const column of columns) {
    // custom question columns hold nested responses rather than plain
    // numbers; buildCustomQuestionFilterConfig covers them
    if (isStrictlyObject(column.question) || !isNumericFilterColumn(column)) {
      continue;
    }
    const { filterDescription, filterSuggestions, filterIcon } = column;
    const key = column.filterField ?? column.field;
    numericKeys[key] = filterDescription;
    filterSuggestions?.forEach((o) =>
      suggestions.push({
        name: o.name,
        icon: filterIcon,
        description: filterDescription,
        filterObject: { key, value: o.id, keyDescription: filterDescription },
      })
    );
  }

  return { numericKeys, suggestions };
};
